import { THICK_WICK_WIDTH_PX, THICK_WICK_BODY_MARGIN_PX } from "../constants";
import { SHADOW_STYLE } from "../../constants";
import { lineWidthPx, bodyWidthPx, centredOffset, buildRectShapes } from "./rectShapes";
import { colorSlotOf } from "./seriesPalette";

const SHAPE_WICK = 0;
const SHAPE_BODY = 1;
export const RECTS_PER_CANDLE = 2;

/**
 * Rectangle instances for candles (and Heikin Ashi candles): a wick
 * (high–low) and a body (open–close) per bar. Prices are relative to
 * `priceBase` (see mainSeries). Pan and zoom do not rebuild them.
 *
 * @param {object[]} bars - { open, high, low, close }
 * @param {number} priceBase
 * @param {number} floatsPerRect - rectRenderer.floatsPerInstance
 * @param {number} [firstIndex] - bar index of bars[0], when only the tail is rebuilt
 * @returns {Float32Array}
 */
export const buildCandleInstances = (bars, priceBase, floatsPerRect, firstIndex = 0) => {
  const data = new Float32Array(bars.length * RECTS_PER_CANDLE * floatsPerRect);
  let offset = 0;

  bars.forEach((bar, position) => {
    const index = firstIndex + position;
    const colorSlot = colorSlotOf(bar);

    data.set(
      [
        index, bar.high - priceBase, bar.low - priceBase, SHAPE_WICK, colorSlot,
        index, bar.open - priceBase, bar.close - priceBase, SHAPE_BODY, colorSlot,
      ],
      offset,
    );
    offset += RECTS_PER_CANDLE * floatsPerRect;
  });

  return data;
};

// Thick (E18): THICK_WICK_WIDTH_PX, but at least THICK_WICK_BODY_MARGIN_PX
// narrower than the body and never thinner than the regular wick
const thickWickWidth = (bodyWidth, pixelRatio, lineWidth) =>
  Math.max(
    lineWidth,
    Math.min(
      Math.floor(THICK_WICK_WIDTH_PX * pixelRatio),
      bodyWidth - Math.floor(THICK_WICK_BODY_MARGIN_PX * pixelRatio),
    ),
  );

/**
 * Wick and body shapes in whole device pixels. The wick is 1 CSS px
 * (Regular) or thicker (Thick); the wick or the body is narrowed by one
 * pixel when needed, so both have the same parity and share one centre.
 *
 * @param {number} barSpacing - CSS px
 * @param {number} pixelRatio
 * @param {string} shadowStyle - SHADOW_STYLE value
 * @returns {{ widths: Float32Array, offsets: Float32Array, minHeight: number }}
 */
export const candleShapes = (barSpacing, pixelRatio, shadowStyle) => {
  const lineWidth = lineWidthPx(pixelRatio);
  let body = bodyWidthPx(barSpacing, pixelRatio, lineWidth);
  let wick =
    shadowStyle === SHADOW_STYLE.THICK ? thickWickWidth(body, pixelRatio, lineWidth) : lineWidth;

  if ((body - wick) % 2 !== 0) {
    if (wick > lineWidth) wick -= 1;
    else body -= 1;
  }

  return buildRectShapes(
    [
      [wick, centredOffset(wick)],
      [body, centredOffset(body)],
    ],
    lineWidth,
  );
};
